<script setup lang="ts">
  import { useRouter, useRoute } from '@gat-ship/lib/router'
  import { computed, onMounted, watch } from 'vue'
  import { Tabs, TabsList } from '../ui/tabs'
  import GatTabItem from './gat-tab-item.vue'
  import { cn } from '../../lib/utils'
  import type { HTMLAttributes } from 'vue'
  import type { TabItem } from './index'
  import { provideGatTab } from './index'

  export interface GatTabProps {
    tabs: TabItem[]
    mode: 'route' | 'model-value'
    class?: HTMLAttributes['class']
  }

  const props = defineProps<GatTabProps>()

  const route = useRoute()

  const modelValue = defineModel<string>({ required: false })

  const activeTab = computed(() => {
    if (typeof modelValue.value === 'string') {
      return modelValue.value
    }
    return route.name?.toString()
  })

  const router = useRouter()

  function goToRoute(tab: TabItem) {
    if (tab.to) {
      router.push(tab.to)
    }
  }

  onMounted(() => {
    if (props.mode === 'route') {
      const currentRoute = route.name?.toString()
      if (currentRoute) {
        const tab = props.tabs.find((tab) => tab.to?.name === currentRoute)
        if (tab) {
          modelValue.value = tab.value
          return
        }
      }
    }
  })

  watch(modelValue, (newVal) => {
    const tab = props.tabs.find((tab) => tab.value === newVal)
    if (newVal && tab) {
      goToRoute(tab)
    }
  })
  provideGatTab({
    modelValue,
    activeTab
  })
</script>
<template>
  <div class="flex grow flex-row overflow-x-hidden" ref="tabsContainer">
    <Tabs :model-value="activeTab" activation-mode="manual">
      <TabsList :class="cn('bg-secondary w-fit', props.class)" ref="tabsList">
        <template v-for="tab in tabs" :key="tab.text">
          <GatTabItem v-bind="tab">
            <template #tab="{ tab }" v-if="$slots.tab">
              <slot name="tab" v-bind="{ tab }"> </slot>
            </template>
          </GatTabItem>
        </template>
      </TabsList>
      <slot name="content"></slot>
    </Tabs>
  </div>
</template>
